import { array, bool, object, number, string } from 'prop-types';
import React, { Component } from 'react';
import { Bling as GPT, Events } from '@atmedia/react-gpt';
import classnames from 'classnames';
import { getSizeProp } from './helpers';

const MAX_REFRESH_COUNT = 10;

class Ad extends Component {
  constructor(props) {
    super(props);
    this.adRef = React.createRef();
    this.state = {
      hasRefresh: !!this.props.refreshInterval,
      refreshCount: 1,
      isSlotEmpty: true,
      creativeId: null
    };
    this.setRefreshTimeout = this.setRefreshTimeout.bind(this);
    this.handleOnBlur = this.handleOnBlur.bind(this);
    this.handleOnFocus = this.handleOnFocus.bind(this);
    this.clearTimers = this.clearTimers.bind(this);
    this.slotRefreshTimeout = undefined;
  }

  componentDidMount() {
    if (this.state.hasRefresh) {
      // TODO: use single blur/focus listeners for ALL instances
      window.addEventListener('blur', this.handleOnBlur, false);
      window.addEventListener('focus', this.handleOnFocus, false);
    }
  }

  componentWillUnmount() {
    // Clear timers and event handlers
    if (this.state.hasRefresh) {
      window.removeEventListener('blur', this.handleOnBlur, false);
      window.removeEventListener('focus', this.handleOnFocus, false);
      this.clearTimers();
    }
  }

  setRefreshTimeout() {
    // Clear any timers that might be set...
    this.clearTimers();
    if (this.state.refreshCount < MAX_REFRESH_COUNT) {
      // Set new timer
      this.slotRefreshTimeout = window.setTimeout(() => {
        const slot = this.adRef.current && this.adRef.current.adSlot;
        if (slot) {
          // Note, updating state that will change the KV targeting will also
          // force a refresh, i.e. we don't need to call GPT.refresh([slot])
          // explicitly (if we do we'll cause a double refresh)
          this.setState({ refreshCount: this.state.refreshCount + 1 });
        }
      }, this.props.refreshInterval * 1000);
    }
  }

  handleOnBlur() {
    this.clearTimers();
  }

  handleOnFocus() {
    this.setRefreshTimeout();
  }

  clearTimers() {
    window.clearTimeout(this.slotRefreshTimeout);
    this.slotRefreshTimeout = undefined;
  }

  render() {
    const { id, unit, sizes, targeting, sizeLabel } = this.props;
    const adClasses = classnames('Ad', {
      [`Ad--${unit.slice(1).replace(/\//g, '-')}`]: true,
      [`Ad--empty`]: this.state.isSlotEmpty,
      [`Ad--rendered`]: !this.state.isSlotEmpty
    });
    return (
      <div
        className={adClasses}
        data-creative-id={this.state.creativeId}
        data-size-label={sizeLabel}
      >
        <GPT
          ref={this.adRef}
          id={id}
          adUnitPath={unit}
          targeting={{
            ...targeting,
            ...(id ? { slotId: id } : null),
            // Note, refresh KV is always set, even if this ad does not have a
            // refresh interval.
            refresh: this.state.refreshCount
          }}
          collapseEmptyDiv
          onSlotRenderEnded={e => {
            if (this.props.showDebug) {
              // prettier-ignore
              console.log(`Ad Render Ended: ${e.slot.getAdUnitPath()}, Empty: ${e.isEmpty}`, e); // eslint-disable-line
            }
            this.setState({
              isSlotEmpty: e.isEmpty,
              creativeId: e.creativeId
            });
            if (this.state.hasRefresh) {
              this.setRefreshTimeout();
            }
          }}
          onSlotOnload={e => {
            if (this.props.showDebug) {
              console.log(`Ad On Load: ${e.slot.getAdUnitPath()}`, e); // eslint-disable-line
            }
          }}
          onSlotVisibilityChanged={({ inViewPercentage }) => {
            if (this.state.hasRefresh) {
              // Special handling for slots with a refresh interval.
              // We do not want to refresh an "out of view" slot, otherwise
              // it will report as an unviewable ad event in Ad Manager. An ad
              // must be 50% in view to be considered viewable.
              if (inViewPercentage < 50) {
                // If the ad goes out of view, we should NOT refresh since it
                // will result in an unviewable impression!
                this.clearTimers();
              } else if (
                inViewPercentage >= 50 &&
                this.slotRefreshTimeout === undefined
              ) {
                // Ad is back in view and does not have a refresh timeout set.
                // Restart the timer.
                this.setRefreshTimeout();
              }
            }
          }}
          onImpressionViewable={e => {
            if (this.props.showDebug) {
              // prettier-ignore
              console.log(`Ad Impression Viewable: ${e.slot.getAdUnitPath()}`, e); // eslint-disable-line
            }
          }}
          {...getSizeProp(sizes)}
        />
      </div>
    );
  }
}

// Expose a subset of GPT static methods
Ad.configure = GPT.configure;
Ad.refresh = GPT.refresh;
Ad.syncCorrelator = GPT.syncCorrelator;
Ad.disableInitialLoad = GPT.disableInitialLoad;
Ad.enableSingleRequest = GPT.enableSingleRequest;

Ad.onReady = callback => {
  GPT.on(Events.READY, callback);
};

Ad.onRender = callback => {
  GPT.on(Events.RENDER, callback);
};

Ad.defaultProps = {
  id: undefined,
  targeting: undefined,
  refreshInterval: undefined,
  sizeLabel: undefined,
  showDebug: false
};

Ad.propTypes = {
  id: string,
  unit: string.isRequired,
  sizes: array.isRequired, // eslint-disable-line
  targeting: object, // eslint-disable-line
  refreshInterval: number,
  sizeLabel: string,
  showDebug: bool
};

export default Ad;
